import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw, ChevronDown, ChevronUp, ArrowLeft } from "lucide-react";

interface GlobalErrorComponentProps {
  error: any;
  reset?: () => void;
}

export default function GlobalErrorComponent({ error, reset }: GlobalErrorComponentProps) {
  const [showDetails, setShowDetails] = useState(false);
  const errorMessage = String(error?.message || error || "").toLowerCase();

  useEffect(() => {
    // Ignore standard network aborts (e.g. from fast user navigation or tab switching)
    if (
      error?.name === "AbortError" ||
      errorMessage.includes("abort") ||
      errorMessage.includes("canceled")
    ) {
      return;
    }

    // Capture the error in Sentry immediately
    console.error("[GlobalErrorComponent] Caught unhandled error:", error);
    Sentry.captureException(error, {
      extra: {
        message: error?.message,
        stack: error?.stack,
        digest: error?.digest,
      },
    });
  }, [error, errorMessage]);

  if (
    error?.name === "AbortError" ||
    errorMessage.includes("abort") ||
    errorMessage.includes("canceled")
  ) {
    return null;
  }

  const handleRetry = () => {
    if (reset) {
      reset();
    } else {
      window.location.reload();
    }
  };

  const handleGoBack = () => {
    window.history.back();
  };

  // Determine user friendly explanation based on error type
  let friendlyReason = "Beklenmedik bir sistemsel hata oluştu.";
  let suggestion = "Lütfen internet bağlantınızı kontrol edip tekrar deneyin.";

  if (
    errorMessage.includes("load failed") ||
    errorMessage.includes("fetch") ||
    errorMessage.includes("network")
  ) {
    friendlyReason = "Sunucu bağlantısı kurulamadı veya ağ isteği iptal edildi.";
    suggestion =
      "Bu durum geçici bir internet kesintisinden veya sayfa yenilenmesinden kaynaklanabilir.";
  } else if (errorMessage.includes("unauthorized") || errorMessage.includes("401")) {
    friendlyReason = "Oturum süreniz dolmuş veya yetkiniz bulunmuyor.";
    suggestion = "Lütfen çıkış yapıp tekrar giriş yapmayı deneyin.";
  } else if (errorMessage.includes("impersonat")) {
    friendlyReason = "Kullanıcı taklidi (impersonation) yönetimi sırasında bir sorun oluştu.";
    suggestion = "Oturum bilgileriniz güncellenirken tarayıcı isteği kesmiş olabilir.";
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 p-4 backdrop-blur-md select-none font-sans">
      <div className="relative w-full max-w-lg rounded-[28px] border border-white/10 bg-zinc-900/95 p-8 shadow-[0_24px_50px_rgba(0,0,0,0.5)] backdrop-blur-2xl text-white transition-all duration-300">
        {/* Error Header */}
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10 border border-red-500/20 text-red-500 animate-pulse">
            <AlertTriangle className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-bold tracking-tight text-red-400">
            Bir Hata Oluştu / Something Went Wrong
          </h2>
          <p className="mt-3 text-sm text-zinc-300 font-medium">{friendlyReason}</p>
          <p className="mt-1.5 text-xs text-zinc-400">{suggestion}</p>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={handleRetry}
            className="flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-500 transition-colors shadow-lg shadow-red-900/20"
          >
            <RefreshCw className="h-4 w-4" />
            Yeniden Dene / Retry
          </button>
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center gap-2 rounded-xl bg-zinc-800 px-5 py-2.5 text-sm font-semibold text-zinc-300 hover:bg-zinc-700 transition-colors border border-white/5"
          >
            <ArrowLeft className="h-4 w-4" />
            Geri Dön / Go Back
          </button>
        </div>

        {/* Technical Details Accordion */}
        <div className="mt-6 border-t border-white/5 pt-4">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex w-full items-center justify-between text-xs text-zinc-500 hover:text-zinc-400 font-semibold uppercase tracking-wider"
          >
            <span>Teknik Detaylar / Technical Details</span>
            {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {showDetails && (
            <div className="mt-3 max-h-40 overflow-y-auto rounded-lg bg-black/40 p-4 border border-white/5  text-[10px] text-zinc-400 leading-relaxed break-all">
              <div className="font-semibold text-red-400 mb-1">
                Error: {String(error?.message || error)}
              </div>
              {error?.stack && (
                <pre className="whitespace-pre-wrap mt-2 text-zinc-500">{error.stack}</pre>
              )}
            </div>
          )}
        </div>

        {/* Automatic Report Indicator */}
        <div className="mt-6 text-center text-[10px] text-zinc-600">
          Bu hata otomatik olarak sistem yöneticisine (Sentry) raporlanmıştır.
        </div>
      </div>
    </div>
  );
}
